import { EnumDialogName } from '@calm/evexia-analytics';
import { SxProps, Theme } from '@rhg/libs/material';
import SimpleDialog, {
    SimpleDialogProps,
} from '@rhg/libs/material/simple-dialog';
import {
    IconColor,
    DialogButton,
    BodyTextAlign,
    RawDialogButton,
} from '@rhg/libs/material/simple-dialog/types';
import {
    createContext,
    MutableRefObject,
    ReactElement,
    ReactNode,
    useCallback,
    useState,
} from 'react';

export const DEFAULT_TIMEOUT = 2000;
export type ToastDialogButton = Omit<DialogButton, 'onClick'>;

export interface ConsentDialogState {
    showConsentDialog: (options: ConsentDialogOptions) => void;
    isConsentDialogOpen: boolean;
}
export const ConsentDialogContext = createContext<ConsentDialogState>(null!);

export interface ConsentDialogOptions {
    trackDialog?: EnumDialogName;
    body?: string;
    bodyTextAlign?: BodyTextAlign;
    content?: ReactElement | ReactElement[] | null;
    title?: string;
    icon?: ReactElement;
    buttons?: (DialogButton | RawDialogButton)[];
    fullScreen?: boolean;
    hasGradientBackground?: boolean;
    iconColor?: IconColor;
    sx?: SxProps<Theme>;
    SimpleDialogProps?: Partial<
        Omit<SimpleDialogProps, 'iconContainerOverride'>
    >;
    timeout?: number;
    triggerCloseRef: MutableRefObject<(() => void) | undefined>;
}

interface Props {
    children: ReactNode;
}

/**
 * This is an unopinionated provider for showing a consent dialog.
 * It can be used to show any consent dialog with the provided options.
 * This allows for cross-flow usages, such as showing a consent dialog in the registration flow or triggered from the profile page
 * As a result, the `onAgree` type callback can be passed to the options.button's onClick prop.
 */
export const ConsentDialogProvider = ({ children }: Props) => {
    const [isOpen, setIsOpen] = useState(false);
    const [options, setOptions] = useState<ConsentDialogOptions | undefined>(
        undefined
    );

    const onClose = useCallback(() => {
        setIsOpen(false);
        setOptions(undefined);
    }, []);

    const showConsentDialog = useCallback(
        (options?: ConsentDialogOptions) => {
            setOptions(options);
            setIsOpen(true);
            if (options?.triggerCloseRef) {
                options.triggerCloseRef.current = onClose;
            }
        },
        [onClose]
    );

    // TODO: In EVE-8765, Add analytics to dialog
    return (
        <>
            <ConsentDialogContext.Provider
                value={{ showConsentDialog, isConsentDialogOpen: isOpen }}
            >
                {children}
            </ConsentDialogContext.Provider>
            <SimpleDialog
                icon={options?.icon}
                iconColor={options?.iconColor}
                trackDialog={options?.trackDialog || EnumDialogName.Toast}
                buttons={
                    options?.buttons ?? [
                        {
                            title: 'I accept (use translation)',
                            onClick: onClose,
                            trackButton: 'NotMapped',
                        },
                    ]
                }
                hideCloseButton={true}
                fullScreen
                isOpen={isOpen}
                title={options?.title}
                onClose={onClose}
                bodyText={options?.body}
                bodyTextAlign={options?.bodyTextAlign}
                content={options?.content}
                {...options?.SimpleDialogProps}
            />
        </>
    );
};
